/**
 * Innovation Review Center (DIGEMP-INNOVATION-REVIEW-CENTER-FRONTEND-062).
 *
 * The operator-facing review workbench for external technology candidates: candidate lifecycle,
 * the MASTER_FLOW minimum review evidence (source, expected benefit, license, security/supply
 * chain, compatibility, evaluation effort), and the human decision trail. It reads only existing,
 * already-gated endpoints — the admin-gated tech-scout discovery registry and catalog
 * (GET /api/tech-scout/discoveries, /catalog) and the audit log (GET /api/activity-log/) that
 * already records every decision (tech_scout router logs tech_discovery_decided). Decisions are
 * recorded through the same admin-gated PATCH /api/tech-scout/discoveries/{id}/decide the
 * Technology Scout page uses — this page adds a review surface, never a new authority.
 *
 * Truthfulness rules:
 *  - a field the evidence does not carry renders as an explicit unknown — never guessed;
 *  - only whitelisted scalar fields are rendered (bounded, typed) — never raw run payloads,
 *    run context, JSON blobs or evidence excerpts;
 *  - a stack-compatibility claim is made only on an exact normalized name match against the
 *    installed catalog.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { activityApi, techScoutApi } from "../services/api";

// The audit log is paged; the server caps limit at 500 (backend/app/routers/activity_log.py).
const TRAIL_LIMIT = 500;

// MASTER_FLOW discovery pipeline (DISCOVERED -> CANDIDATE -> SECURITY_REVIEW -> EVALUATING ->
// APPROVED/REJECTED -> ACTIVE/SUSPENDED/DEPRECATED) read over the stored registry vocabulary.
// One icon, label and color per stage, all visually distinct, so a merely-discovered candidate
// can never read as approved or active. Unknown values degrade to an explicit unknown.
export const LIFECYCLE_META = {
  discovered:              { icon: "◉", label: "נגלה",                 cls: "bg-sky-100 text-sky-800 border-sky-200" },
  candidate:               { icon: "◇", label: "מועמד",                cls: "bg-violet-100 text-violet-800 border-violet-200" },
  evaluating:              { icon: "◷", label: "בהערכה",               cls: "bg-amber-100 text-amber-800 border-amber-200" },
  security_review:         { icon: "⛨", label: "סקירת אבטחה",          cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  review_required:         { icon: "◔", label: "נדרשת סקירה",          cls: "bg-orange-100 text-orange-800 border-orange-200" },
  human_decision_required: { icon: "⚑", label: "נדרשת החלטה אנושית",   cls: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  approved:                { icon: "✔", label: "אושר",                 cls: "bg-teal-100 text-teal-800 border-teal-200" },
  rejected:                { icon: "⊘", label: "נדחה",                 cls: "bg-red-100 text-red-700 border-red-200" },
  active:                  { icon: "●", label: "פעיל",                 cls: "bg-green-100 text-green-700 border-green-200" },
  integrated:              { icon: "◆", label: "משולב בייצור",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  ignored:                 { icon: "⤺", label: "הוסתר (נשמר במאגר)",   cls: "bg-slate-100 text-slate-500 border-slate-200" },
  unknown:                 { icon: "?", label: "מצב לא ידוע",          cls: "bg-gray-100 text-gray-500 border-gray-200" },
};

// Board order: the review pipeline, then the terminal/hidden lanes.
export const LIFECYCLE_ORDER = [
  "discovered", "candidate", "evaluating", "security_review", "review_required",
  "human_decision_required", "approved", "rejected", "active", "integrated", "ignored", "unknown",
];

// The stages the registry vocabulary can hold today get a permanent board column; the staged
// names a future payload may declare only appear when they actually carry candidates.
const ALWAYS_SHOWN_STAGES = new Set([
  "discovered", "candidate", "evaluating", "approved", "rejected", "active", "ignored", "unknown",
]);

const LIFECYCLE_LEGEND =
  "נגלה = טרם הוחלט · מועמד = במעקב, טרם הוערך · בהערכה = פיילוט/הערכה בלבד · אושר = עבר סקירה, טרם הופעל · פעיל = אומץ לשימוש · נדחה = נסגר עם נימוק · הוסתר = הוסר מהמאגר הפעיל אך נשמר לחיפוש";

// The registry vocabulary stored today (tech_scout router _VALID_STATUSES plus the legacy
// adopted/ignored values). A payload that declares a canonical lifecycle state wins.
export const STATUS_TO_LIFECYCLE = {
  pending: "discovered",
  watching: "candidate",
  piloting: "evaluating",
  adopted: "active",
  rejected: "rejected",
  ignored: "ignored",
};

const DECISION_META = {
  use:    { label: "השתמש", color: "bg-green-100 text-green-700 border-green-300" },
  adapt:  { label: "התאם", color: "bg-blue-100 text-blue-700 border-blue-300" },
  watch:  { label: "עקוב", color: "bg-amber-100 text-amber-700 border-amber-300" },
  ignore: { label: "התעלם", color: "bg-gray-100 text-gray-500 border-gray-300" },
  reject: { label: "דחה", color: "bg-red-100 text-red-600 border-red-300" },
};

const SOURCE_LABELS = {
  scout_run: "סריקת AI אוטומטית",
  manual: "הוזן ידנית",
  feed: "מקור חיצוני",
};

const CATEGORY_LABELS = {
  backend: "צד שרת",
  ai: "AI / LLM",
  security: "אבטחה",
  infra: "תשתית",
  dev: "פיתוח",
  rpa: "אוטומציה",
};

const REVIEW_TONE_CLS = {
  clear: "bg-green-50 text-green-700 border-green-200",
  flagged: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  neutral: "bg-gray-50 text-gray-600 border-gray-200",
};

const REVIEW_CLEAR = new Set(["ok", "passed", "pass", "approved", "clear", "cleared", "clean"]);
const REVIEW_FLAGGED = new Set(["flagged", "fail", "failed", "blocked", "risk", "risky", "rejected"]);
const REVIEW_PENDING = new Set(["pending", "in_progress", "reviewing", "requested", "scheduled"]);

export function reviewTone(status) {
  const key = typeof status === "string" ? status.toLowerCase() : "";
  if (REVIEW_CLEAR.has(key)) return "clear";
  if (REVIEW_FLAGGED.has(key)) return "flagged";
  if (REVIEW_PENDING.has(key)) return "pending";
  return "neutral";
}

export function boundedText(value, max) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

function extraOf(disc) {
  const extra = disc?.extra;
  return extra && typeof extra === "object" && !Array.isArray(extra) ? extra : {};
}

function pickText(source, keys, max) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = boundedText(source[key], max);
    if (value) return value;
  }
  return null;
}

function readMaybeText(value, keys, max) {
  return typeof value === "string" ? boundedText(value, max) : pickText(value, keys, max);
}

export function stageKeyOf(raw) {
  const key = boundedText(raw, 40)?.toLowerCase();
  if (!key) return "unknown";
  if (LIFECYCLE_META[key]) return key;
  return STATUS_TO_LIFECYCLE[key] || "unknown";
}

export function reviewStageOf(disc) {
  const extra = extraOf(disc);
  const declared = boundedText(disc?.lifecycle_state, 40) || boundedText(extra.lifecycle_state, 40);
  return stageKeyOf(declared || disc?.status);
}

export function expectedBenefitOf(disc) {
  const extra = extraOf(disc);
  return pickText(disc, ["expected_benefit"], 300)
    || readMaybeText(extra.expected_benefit, ["text", "summary", "description"], 300)
    || readMaybeText(extra.benefit, ["text", "summary", "description"], 300)
    || boundedText(disc?.relevance_summary, 300);
}

export function licenseOf(disc) {
  const raw = disc?.license ?? extraOf(disc).license ?? extraOf(disc).license_name;
  if (typeof raw === "string") {
    const value = boundedText(raw, 80);
    return value ? { value, status: null } : null;
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const value = pickText(raw, ["spdx_id", "id", "key", "name", "type", "license"], 80);
    const status = pickText(raw, ["status", "review", "review_status", "state"], 60);
    return value || status ? { value, status } : null;
  }
  return null;
}

export function securityReviewOf(disc) {
  const raw = disc?.security_review ?? extraOf(disc).security_review ?? extraOf(disc).security;
  if (typeof raw === "string") {
    const status = boundedText(raw, 60);
    return status ? { status, note: null } : null;
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const status = pickText(raw, ["status", "state", "result", "review_status"], 60);
    const note = pickText(raw, ["note", "summary", "details", "reason"], 240);
    return status || note ? { status, note } : null;
  }
  return null;
}

export function compatibilityOf(disc) {
  return readMaybeText(disc?.compatibility, ["requirements", "text", "summary", "notes"], 300)
    || readMaybeText(extraOf(disc).compatibility, ["requirements", "text", "summary", "notes"], 300);
}

export function upstreamOf(disc) {
  const extra = extraOf(disc);
  return pickText(disc, ["upstream", "author", "repository"], 120)
    || pickText(extra, ["upstream", "author", "repository", "repo"], 120);
}

export function replacedCodeOf(disc) {
  const extra = extraOf(disc);
  return readMaybeText(extra.replaces, ["text", "name", "description"], 200)
    || readMaybeText(extra.potentially_replaces, ["text", "name", "description"], 200)
    || readMaybeText(extra.replaced_custom_code, ["text", "name", "description"], 200);
}

export function evaluationEffortOf(disc) {
  const extra = extraOf(disc);
  return readMaybeText(extra.evaluation_effort, ["text", "estimate", "summary"], 120)
    || readMaybeText(extra.effort, ["text", "estimate", "summary"], 120);
}

export function isSafeHttpUrl(url) {
  const value = boundedText(url, 512);
  return !!(value && /^https?:\/\//i.test(value));
}

function normalizedKey(value) {
  return typeof value === "string" ? value.toLowerCase().replace(/[^a-z0-9]+/g, "") : "";
}

// Compatibility claim against the installed stack: exact normalized name match only, and only
// for entries the catalog reports as installed — anything fuzzier would be a guess.
export function installedEntryFor(catalogEntries, technologyName) {
  const key = normalizedKey(technologyName);
  if (!key) return null;
  for (const entry of catalogEntries || []) {
    if (!entry?.installed_version) continue;
    if (normalizedKey(entry.import_name) === key || normalizedKey(entry.display_name) === key) {
      return entry;
    }
  }
  return null;
}

export function fmtDate(iso, withTime = false) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(
    "he-IL",
    withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" },
  ).format(d);
}

function ageLabel(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "נוסף/ה היום";
  return `במאגר ${days} ימים`;
}

function decisionLabel(value) {
  // The tech_scout router writes "none" as the old_value of a first-ever decision.
  if (!value || value === "none") return "ללא החלטה";
  return DECISION_META[value]?.label || "החלטה לא מזוהה";
}

export function StageBadge({ stageKey, withLabel = true }) {
  const meta = LIFECYCLE_META[stageKey] || LIFECYCLE_META.unknown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${meta.cls}`}>
      <span aria-hidden="true">{meta.icon}</span>
      {withLabel ? meta.label : null}
    </span>
  );
}

function Unknown() {
  return <span className="text-gray-400">לא ידוע</span>;
}

function EvidenceRow({ label, testid, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-2 border-b border-gray-100 last:border-b-0" data-testid={testid}>
      <dt className="text-xs text-gray-500 sm:w-44 shrink-0">{label}</dt>
      <dd className="text-sm text-gray-700 min-w-0">{children}</dd>
    </div>
  );
}

function ReviewStatusChip({ status }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${REVIEW_TONE_CLS[reviewTone(status)]}`}>
      {status}
    </span>
  );
}

export function DecisionTrail({ candidate, auditEntries, truncated }) {
  const items = useMemo(() => {
    const out = [];

    for (const entry of auditEntries || []) {
      const action = typeof entry?.action === "string" ? entry.action : "";
      const at = entry?.created_at;
      const who = boundedText(entry?.user_display_name, 80) || "משתמש לא ידוע";
      if (action === "tech_discovery_decided") {
        out.push({
          key: `audit-${entry.id}`,
          kind: "decision",
          at,
          who,
          from: entry.old_value,
          to: entry.new_value,
        });
      } else if (action === "tech_discovery_added") {
        out.push({ key: `audit-${entry.id}`, kind: "added", at, who });
      } else if (action) {
        // An action the vocabulary does not know yet stays visible without exposing the raw id.
        out.push({ key: `audit-${entry.id}`, kind: "other", at, who });
      }
    }

    const history = Array.isArray(extraOf(candidate).lifecycle_history)
      ? extraOf(candidate).lifecycle_history
      : [];
    history.forEach((entry, index) => {
      if (!entry || typeof entry !== "object") return;
      out.push({
        key: `stage-${index}`,
        kind: "stage",
        at: entry.at,
        who: boundedText(entry.by, 80) || "משתמש לא ידוע",
        from: entry.from,
        to: entry.to,
        reason: boundedText(entry.reason, 240),
      });
    });

    return out.sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
  }, [candidate, auditEntries]);

  const decided = !!candidate?.decision;

  return (
    <section aria-label="מסלול ההחלטות" className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-700">מסלול ההחלטות</h2>
        <span className="text-xs text-gray-400">מבוסס על יומן הפעולות של המאגר</span>
      </div>

      {items.length === 0 && !decided ? (
        <p className="text-sm text-gray-400 text-center py-3" data-testid="irc-trail-empty">
          {truncated
            ? "מוצגות 500 הרשומות האחרונות של פעולות במאגר הגילויים; לא נמצאה בהן רשומה למועמד זה."
            : "אין תיעוד החלטות אנושיות לרשומה זו"}
        </p>
      ) : (
        <ol className="space-y-2">
          {decided && (
            <li className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2" data-testid="irc-trail-current">
              <span className="text-gray-400 text-xs pt-0.5 shrink-0" aria-hidden="true">◦</span>
              <div className="min-w-0">
                <p className="text-sm text-gray-700">
                  ההחלטה הנוכחית: <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border font-medium ${DECISION_META[candidate.decision]?.color || "bg-gray-100 text-gray-500 border-gray-300"}`}>{decisionLabel(candidate.decision)}</span>
                </p>
                {boundedText(candidate.decision_reason, 300) && (
                  <p className="text-xs text-gray-500 mt-0.5">{boundedText(candidate.decision_reason, 300)}</p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  {boundedText(candidate.decided_by, 80) || "משתמש לא ידוע"} · {fmtDate(candidate.decided_at, true)}
                </p>
              </div>
            </li>
          )}
          {items.map((item) => (
            <li key={item.key} className="flex items-start gap-3 border-b border-gray-50 last:border-b-0 pb-2 last:pb-0" data-testid="irc-trail-entry">
              <span className="text-gray-400 text-xs pt-0.5 shrink-0" aria-hidden="true">
                {item.kind === "stage" ? "⇢" : item.kind === "decision" ? "✔" : "＋"}
              </span>
              <div className="min-w-0">
                <p className="text-sm text-gray-700">
                  {item.kind === "decision" ? (
                    <>
                      החלטה עודכנה: <span className="font-medium">{decisionLabel(item.from)}</span>
                      <span className="text-gray-400 mx-1" aria-hidden="true">←</span>
                      <span className="font-medium">{decisionLabel(item.to)}</span>
                    </>
                  ) : item.kind === "stage" ? (
                    <>
                      מעבר שלב: <span className="font-medium">{(LIFECYCLE_META[stageKeyOf(item.from)] || LIFECYCLE_META.unknown).label}</span>
                      <span className="text-gray-400 mx-1" aria-hidden="true">←</span>
                      <span className="font-medium">{(LIFECYCLE_META[stageKeyOf(item.to)] || LIFECYCLE_META.unknown).label}</span>
                    </>
                  ) : item.kind === "added" ? (
                    "נוסף/ה למאגר הגילויים"
                  ) : (
                    "עודכן/ה במאגר הגילויים"
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {item.who} · {fmtDate(item.at, true)}
                </p>
                {item.reason && <p className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1 mt-1">{item.reason}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}

      {truncated && items.length > 0 && (
        <p className="text-xs text-gray-400">מוצגות 500 הרשומות האחרונות של פעולות במאגר הגילויים.</p>
      )}
    </section>
  );
}

function DecisionPanel({ candidate, onDecide, deciding, error, done }) {
  const [redeciding, setRedeciding] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [reason, setReason] = useState("");

  const decided = !!candidate.decision;

  function handleDecide(decision) {
    if (decision === "ignore" || decision === "reject") {
      setPendingDecision(decision);
      setShowReason(true);
    } else {
      onDecide({ decision });
      setRedeciding(false);
    }
  }

  function submitWithReason() {
    onDecide({ decision: pendingDecision, decision_reason: reason.trim() || undefined });
    setShowReason(false);
    setReason("");
    setPendingDecision(null);
    setRedeciding(false);
  }

  function cancelReason() {
    setShowReason(false);
    setPendingDecision(null);
    setReason("");
  }

  return (
    <section aria-label="החלטה אנושית" className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-700">החלטה אנושית</h2>
        <span className="text-xs text-gray-400">ההחלטה נרשמת ביומן הפעולות ומופיעה במסלול ההחלטות</span>
      </div>

      {decided && (
        <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 space-y-1">
          <p className="text-sm text-gray-700">
            ההחלטה הנוכחית:{" "}
            <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border font-medium ${DECISION_META[candidate.decision]?.color || "bg-gray-100 text-gray-500 border-gray-300"}`}>
              {decisionLabel(candidate.decision)}
            </span>
          </p>
          {boundedText(candidate.decision_reason, 300) && (
            <p className="text-xs text-gray-500">{boundedText(candidate.decision_reason, 300)}</p>
          )}
          <p className="text-xs text-gray-400">
            {boundedText(candidate.decided_by, 80) || "משתמש לא ידוע"} · {fmtDate(candidate.decided_at, true)}
          </p>
        </div>
      )}

      {(!decided || redeciding) && !showReason && (
        <div className="flex gap-2 flex-wrap">
          {Object.entries(DECISION_META).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              disabled={deciding}
              onClick={() => handleDecide(key)}
              className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors hover:opacity-80 disabled:opacity-40 ${meta.color}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
      )}

      {decided && !redeciding && !showReason && (
        <button type="button" onClick={() => setRedeciding(true)} className="text-xs text-brand-600 hover:underline">
          שנה החלטה
        </button>
      )}

      {showReason && (
        <div className="space-y-2">
          <label className="text-xs text-gray-500 block" htmlFor="irc-reason">נימוק להחלטה {DECISION_META[pendingDecision]?.label}</label>
          <input
            id="irc-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="סיבה (אופציונלי)"
            className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
            dir="rtl"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitWithReason}
              disabled={deciding}
              className={`text-xs px-3 py-1 rounded-full border font-medium disabled:opacity-40 ${DECISION_META[pendingDecision]?.color}`}
            >
              אשר
            </button>
            <button type="button" onClick={cancelReason} className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-600">
              ביטול
            </button>
          </div>
        </div>
      )}

      {deciding && <p className="text-xs text-gray-400" aria-live="polite">שומר החלטה…</p>}
      {done && !deciding && <p className="text-xs text-green-600" aria-live="polite">ההחלטה נרשמה ומופיעה במסלול ההחלטות.</p>}
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
    </section>
  );
}

export default function InnovationReviewCenterPage() {
  const qc = useQueryClient();
  const [stageFilter, setStageFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // Scoped to the candidate the decision was recorded for, so the feedback can never appear on
  // another candidate's review card after switching in the queue.
  const [decideFeedback, setDecideFeedback] = useState(null);

  const discoveriesQuery = useQuery({
    queryKey: ["tech-discoveries"],
    queryFn: () => techScoutApi.discoveries(),
    staleTime: 30_000,
  });
  const registryReady = discoveriesQuery.isSuccess;

  const catalogQuery = useQuery({
    queryKey: ["tech-scout-catalog"],
    queryFn: techScoutApi.catalog,
    staleTime: 60_000,
    enabled: registryReady,
  });

  // Same audit log the ActivityLogPage reads; decisions are stored there by the tech_scout
  // router's log_activity calls. entity_id is not a server filter, so rows are grouped here.
  const trailQuery = useQuery({
    queryKey: ["tech-discovery-activity"],
    queryFn: () => activityApi.list({ entity_type: "tech_discovery", limit: TRAIL_LIMIT }),
    staleTime: 30_000,
    enabled: registryReady,
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, payload }) => techScoutApi.decide(id, payload),
    onMutate: () => setDecideFeedback(null),
    onSuccess: (_data, variables) => {
      setDecideFeedback({ id: String(variables.id), kind: "done" });
      qc.invalidateQueries({ queryKey: ["tech-discoveries"] });
      qc.invalidateQueries({ queryKey: ["tech-discovery-activity"] });
    },
    onError: (err, variables) => {
      const detail = err?.response?.data?.detail;
      setDecideFeedback({
        id: String(variables.id),
        kind: "error",
        message: typeof detail === "string" && detail ? detail : "שגיאה בשמירת ההחלטה",
      });
    },
  });

  const candidates = useMemo(() => discoveriesQuery.data ?? [], [discoveriesQuery.data]);

  const ordered = useMemo(() => {
    return [...candidates].sort((a, b) => {
      const aOpen = a.decision ? 1 : 0;
      const bOpen = b.decision ? 1 : 0;
      if (aOpen !== bOpen) return aOpen - bOpen;                       // undecided candidates first
      const byAttention = (b.attention_score ?? 0) - (a.attention_score ?? 0);
      if (byAttention !== 0) return byAttention;
      return (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0);
    });
  }, [candidates]);

  const counts = useMemo(() => {
    const out = {};
    for (const candidate of candidates) {
      const stage = reviewStageOf(candidate);
      out[stage] = (out[stage] || 0) + 1;
    }
    return out;
  }, [candidates]);

  const categories = useMemo(() => {
    const set = new Set();
    for (const candidate of candidates) {
      const value = boundedText(candidate?.category, 50);
      if (value) set.add(value);
    }
    return [...set].sort();
  }, [candidates]);

  const queue = useMemo(() => {
    return ordered.filter((candidate) => {
      if (stageFilter && reviewStageOf(candidate) !== stageFilter) return false;
      if (categoryFilter && boundedText(candidate?.category, 50) !== categoryFilter) return false;
      return true;
    });
  }, [ordered, stageFilter, categoryFilter]);

  const selected = useMemo(() => {
    return ordered.find((candidate) => String(candidate?.id) === String(selectedId)) || ordered[0] || null;
  }, [ordered, selectedId]);

  const trailByEntity = useMemo(() => {
    const map = new Map();
    for (const entry of trailQuery.data ?? []) {
      const id = String(entry?.entity_id ?? "");
      if (!id) continue;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(entry);
    }
    return map;
  }, [trailQuery.data]);

  const awaitingCount = candidates.filter((c) => !c.decision).length;
  const urgentCount = candidates.filter((c) => !c.decision && (c.attention_score ?? 0) >= 7).length;
  const decidedCount = candidates.length - awaitingCount;

  const status403 = discoveriesQuery.error?.response?.status === 403;
  const trailTruncated = (trailQuery.data?.length ?? 0) >= TRAIL_LIMIT;

  // ── states ──────────────────────────────────────────────────────────────────
  if (discoveriesQuery.isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-4" dir="rtl">
        <div className="animate-pulse h-10 bg-gray-100 rounded-xl" />
        <div className="animate-pulse h-24 bg-gray-100 rounded-xl" />
        <div className="animate-pulse h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (discoveriesQuery.isError) {
    return (
      <div className="max-w-3xl mx-auto" dir="rtl">
        <div className="bg-white border rounded-xl shadow-sm p-8 text-center space-y-2">
          <p className="text-2xl" aria-hidden="true">🔒</p>
          <h1 className="text-lg font-semibold text-gray-800">מרכז סקירת חדשנות</h1>
          {status403 ? (
            <p role="alert" className="text-sm text-gray-500">אין הרשאה לצפות במרכז סקירת החדשנות (נדרשת הרשאת מנהל).</p>
          ) : (
            <>
              <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את מאגר הגילויים.</p>
              <button
                type="button"
                onClick={() => discoveriesQuery.refetch()}
                className="text-sm text-brand-600 hover:underline"
              >
                נסה שוב
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const selectedStage = selected ? reviewStageOf(selected) : null;
  const benefit = selected ? expectedBenefitOf(selected) : null;
  const license = selected ? licenseOf(selected) : null;
  const security = selected ? securityReviewOf(selected) : null;
  const compatibility = selected ? compatibilityOf(selected) : null;
  const upstream = selected ? upstreamOf(selected) : null;
  const replaced = selected ? replacedCodeOf(selected) : null;
  const effort = selected ? evaluationEffortOf(selected) : null;
  const installed = selected ? installedEntryFor(catalogQuery.data?.entries, selected.technology_name) : null;
  const extra = selected ? extraOf(selected) : {};
  const evidenceHistory = selected && Array.isArray(extra.evidence_history) ? extra.evidence_history : [];
  const sourceLabel = selected ? SOURCE_LABELS[selected.source] : null;
  const runId = selected ? boundedText(selected.scout_run_id, 36) : null;

  return (
    <div className="max-w-6xl mx-auto space-y-5" dir="rtl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">מרכז סקירת חדשנות 🔬</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            מחזור חיים, ראיות סקירה ומסלול החלטות אנושיות על מועמדים טכנולוגיים חיצוניים
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/tech-scout" className="text-sm text-brand-600 hover:underline">סריקות וניהול המאגר ←</Link>
          <button
            type="button"
            onClick={() => discoveriesQuery.refetch()}
            className="text-sm border rounded-lg px-3 py-1.5 text-gray-600 hover:bg-gray-50"
          >
            רענן
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white border rounded-xl px-5 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <span className="text-gray-500">סה"כ מועמדים: <span className="font-semibold text-gray-800">{candidates.length}</span></span>
        <span className="text-gray-500">ממתינים להחלטה: <span className="font-semibold text-amber-700">{awaitingCount}</span></span>
        {urgentCount > 0 && (
          <span className="text-amber-700">⚡ דחופים: <span className="font-semibold">{urgentCount}</span></span>
        )}
        <span className="text-gray-500">הוחלטו: <span className="font-semibold text-gray-800">{decidedCount}</span></span>
      </div>

      {/* Lifecycle board */}
      <section aria-label="מחזור החיים של המועמדים" className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-gray-700">מחזור החיים של המועמדים</h2>
          {stageFilter && (
            <button type="button" onClick={() => setStageFilter(null)} className="text-xs text-brand-600 hover:underline">
              הצג את כל השלבים
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {LIFECYCLE_ORDER
            .filter((stage) => ALWAYS_SHOWN_STAGES.has(stage) || (counts[stage] || 0) > 0)
            .map((stage) => {
              const meta = LIFECYCLE_META[stage];
              const active = stageFilter === stage;
              return (
                <button
                  key={stage}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStageFilter(active ? null : stage)}
                  className={`text-right border rounded-xl px-3 py-2 transition-colors ${meta.cls} ${active ? "ring-2 ring-offset-1 ring-brand-400" : "hover:opacity-80"}`}
                >
                  <span className="block text-xs font-medium">
                    <span aria-hidden="true">{meta.icon}</span> {meta.label}
                  </span>
                  <span className="block text-xl font-bold mt-0.5">{counts[stage] || 0}</span>
                </button>
              );
            })}
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">{LIFECYCLE_LEGEND}</p>
      </section>

      {candidates.length === 0 ? (
        <div className="bg-white border rounded-xl shadow-sm p-8 text-center" data-testid="irc-empty">
          <p className="text-sm text-gray-400">אין עדיין מועמדים במאגר הגילויים — סריקת AI חדשה תיצור מועמדים לסקירה.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Review queue */}
          <section aria-label="תור הסקירה" className="lg:col-span-2 bg-white border rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold text-gray-700">תור הסקירה</h2>
              <span className="text-xs text-gray-400">{queue.length} מועמדים</span>
            </div>

            <div className="flex gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => setCategoryFilter(null)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${!categoryFilter ? "bg-brand-600 text-white border-brand-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
              >
                הכל
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setCategoryFilter(categoryFilter === category ? null : category)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${categoryFilter === category ? "bg-brand-600 text-white border-brand-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                >
                  {CATEGORY_LABELS[category] || category}
                </button>
              ))}
            </div>

            {queue.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">אין מועמדים בסינון הנוכחי</p>
            ) : (
              <ul className="space-y-2 max-h-[32rem] overflow-y-auto pl-0">
                {queue.map((candidate) => {
                  const stage = reviewStageOf(candidate);
                  const isSelected = selected && String(selected.id) === String(candidate.id);
                  const age = ageLabel(candidate.created_at);
                  return (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(candidate.id)}
                        aria-pressed={isSelected}
                        className={`w-full text-right border rounded-xl px-3 py-2 space-y-1 transition-colors ${isSelected ? "border-brand-400 ring-1 ring-brand-200 bg-brand-50/40" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm text-gray-800 truncate">{boundedText(candidate.technology_name, 200) || "ללא שם"}</span>
                          <StageBadge stageKey={stage} withLabel={false} />
                        </span>
                        <span className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
                          <span>{LIFECYCLE_META[stage].label}</span>
                          {boundedText(candidate.category, 50) && <span>· {CATEGORY_LABELS[candidate.category] || candidate.category}</span>}
                          {age && <span>· {age}</span>}
                          {(candidate.attention_score ?? 0) >= 7 && <span className="text-amber-700">· ⚡ דחוף</span>}
                          {candidate.decision && <span>· הוחלט: {decisionLabel(candidate.decision)}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Review detail */}
          {selected && (
            <div className="lg:col-span-3 space-y-5">
              <section aria-label="פרטי מועמד" className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-800 break-words">{boundedText(selected.technology_name, 200) || "ללא שם"}</h2>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <StageBadge stageKey={selectedStage} />
                      {boundedText(selected.category, 50) && (
                        <span className="text-xs text-gray-400 border border-gray-200 rounded-full px-2 py-0.5">{CATEGORY_LABELS[selected.category] || selected.category}</span>
                      )}
                      {(selected.attention_score ?? 0) >= 7 && (
                        <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">⚡ דחיפות {selected.attention_score}</span>
                      )}
                    </div>
                  </div>
                  {isSafeHttpUrl(selected.url) && (
                    <a href={selected.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all">
                      {boundedText(selected.url, 512)}
                    </a>
                  )}
                </div>
                {boundedText(selected.description, 500) && (
                  <p className="text-sm text-gray-600">{boundedText(selected.description, 500)}</p>
                )}
                <p className="text-xs text-gray-400">
                  נוצר/ה: {fmtDate(selected.created_at)}
                  {ageLabel(selected.created_at) && <span> · {ageLabel(selected.created_at)}</span>}
                  {runId && /^[0-9a-f-]{36}$/i.test(runId) && (
                    <span> · <Link to={`/agent-runs/${runId}`} className="text-brand-600 hover:underline">ריצת הסריקה</Link></span>
                  )}
                </p>
              </section>

              <section aria-label="סקירת ראיות" className="bg-white border rounded-xl shadow-sm p-5">
                <h2 className="font-semibold text-gray-700 mb-1">סקירת ראיות</h2>
                <p className="text-xs text-gray-400 mb-2">לפי דרישות התיעוד של MASTER_FLOW למועמד חיצוני; שדות שלא תועדו מוצגים כ״לא ידוע״.</p>
                <dl className="space-y-0">
                  <EvidenceRow label="מקור" testid="irc-dim-source">
                    {sourceLabel || (boundedText(selected.source, 100) ? "מקור אחר" : <Unknown />)}
                    {boundedText(extra.last_seen, 40) && <span className="text-xs text-gray-400"> · נראה לאחרונה: {fmtDate(extra.last_seen)}</span>}
                  </EvidenceRow>
                  <EvidenceRow label="פרויקט/מחבר במעלה הזרם" testid="irc-dim-upstream">
                    {upstream || <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="תועלת צפויה" testid="irc-dim-benefit">
                    {benefit || <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="קוד מותאם שנחסך" testid="irc-dim-replaces">
                    {replaced || <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="רישיון" testid="irc-dim-license">
                    {license ? (
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        {license.value || <Unknown />}
                        <ReviewStatusChip status={license.status} />
                      </span>
                    ) : <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="אבטחה ושרשרת אספקה" testid="irc-dim-security">
                    {security ? (
                      <span className="space-y-1 block">
                        <ReviewStatusChip status={security.status} />
                        {security.note && <span className="block text-xs text-gray-500">{security.note}</span>}
                        {!security.status && !security.note && <Unknown />}
                      </span>
                    ) : <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="תאימות" testid="irc-dim-compat">
                    <span className="space-y-1 block">
                      {compatibility && <span className="block">{compatibility}</span>}
                      {installed ? (
                        <span className="inline-flex items-center text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                          מותקן במחסנית הפלטפורמה: {boundedText(installed.display_name, 120) || installed.import_name} v{installed.installed_version}
                        </span>
                      ) : null}
                      {!compatibility && !installed && <Unknown />}
                    </span>
                  </EvidenceRow>
                  <EvidenceRow label="גרסה/תג שצוינו" testid="irc-dim-version">
                    {boundedText(selected.version_noted, 50) || <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="מאמץ הערכה משוער" testid="irc-dim-effort">
                    {effort || <Unknown />}
                  </EvidenceRow>
                  <EvidenceRow label="ראיות סריקה" testid="irc-dim-evidence">
                    {evidenceHistory.length > 0 ? (
                      <span>
                        נצפה {evidenceHistory.length} פעמים בסריקות AI
                        {boundedText(extra.first_seen, 40) && <span className="text-xs text-gray-400"> · נצפה לראשונה: {fmtDate(extra.first_seen)}</span>}
                      </span>
                    ) : <Unknown />}
                  </EvidenceRow>
                </dl>
              </section>

              <DecisionPanel
                key={String(selected.id)}
                candidate={selected}
                deciding={decideMutation.isPending && String(decideMutation.variables?.id) === String(selected.id)}
                error={decideFeedback?.id === String(selected.id) && decideFeedback.kind === "error" ? decideFeedback.message : null}
                done={decideFeedback?.id === String(selected.id) && decideFeedback.kind === "done"}
                onDecide={(payload) => decideMutation.mutate({ id: selected.id, payload })}
              />

              <DecisionTrail
                candidate={selected}
                auditEntries={trailByEntity.get(String(selected.id)) || []}
                truncated={trailTruncated}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
